import { CloseOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card, Col, message, Modal, Row, Spin, Typography, Input, Table } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import { ACL } from 'components/common';
import { getVendorBillDetailsV2 } from 'store/vendorBillSliceV2';
import {
	acceptVendorBillChanges,
	rejectVendorBillChanges,
	getInvoiceEditApprovalDetailsV2,
	getVendorBillFieldChangesHistoryV2,
} from 'store/approvalSliceV2';
import {
	getInvoiceEditApprovalDetailsV1,
	getVendorBillFieldChangesHistoryV1,
	acceptVendorBillChangesV1,
	rejectVendorBillChangesV1,
} from 'store/approvalSlice';
import { getUIVersion } from 'utils/router-static-data';
import { getSignedUrl } from 'store/commonSlice';

const { Title } = Typography;
const { TextArea } = Input;

const APPROVALS_INVOICE_EDIT_PATH = '/approvals/invoice-edit-approval';

const PageContainer = styled(Box)`
	padding: 24px;
	background: ${(props) => (props.$fromApproval ? '#fff' : '#f5f5f5')};
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const HeaderRight = styled.div`
	display: flex;
	gap: 12px;
`;

const AuditId = styled(Title)`
	margin: 0 !important;
	font-size: 20px;
	font-weight: 600;
`;

const CloseIconButton = styled(Button)`
	border: none;
	box-shadow: none;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;

	&:hover {
		background-color: #f5f5f5;
	}
`;

const ActionButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e8e8e8;
`;

const RejectButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
	}
`;

const ApproveButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #1890ff;
	color: #fff;
	border: 1.5px solid #1890ff;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #40a9ff;
		border-color: #40a9ff;
	}

	&:active {
		background: #1890ff;
		border-color: #1890ff;
	}
`;

const DetailsCard = styled(Card)`
	margin-top: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FieldLabel = styled(Text)`
	font-size: 14px;
	color: #666;
	font-weight: 400;
	display: block;
	margin-bottom: 8px;
`;

const FieldValue = styled(Text)`
	font-size: 16px;
	color: #000;
	font-weight: 500;
	display: block;
`;

const ChangesCard = styled(Card)`
	margin-top: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const formatDate = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY');
};

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	return `₹ ${Number(value).toLocaleString('en-IN', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

function InvoiceEditApprovalDetailsPageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [editHistory, setEditHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
	const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
	const [imagePreviewLoading, setImagePreviewLoading] = useState(false);
	const [imagePreviewName, setImagePreviewName] = useState('');

	// Check if accessed from approval page
	const isApprovalRoute = location.pathname.startsWith('/approvals/invoice-edit-approval');
	const fromApproval = location.state?.fromApproval || isApprovalRoute;
	const auditIdFromState = location.state?.auditId || null;
	const entityIdFromState = location.state?.entityId || null;
	// If accessed from approval route, the id param is the entity_id (vendor bill ID)
	// audit_id is passed in state
	const entityId = isApprovalRoute ? id || entityIdFromState : id;
	const auditId = auditIdFromState;

	useEffect(() => {
		if (entityId) {
			fetchDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityId]);

	const fetchVendorBillDetailsOptional = async () => {
		const uiVersion = getUIVersion();
		const isV2UI = uiVersion === 'new';

		// Skip vendor bill details call for old UI to avoid v2 API call
		// Approval data already contains all necessary fields
		if (!isV2UI) {
			return {};
		}

		try {
			const vendorBillResponse = await dispatch(
				getVendorBillDetailsV2({ id: Number(entityId) })
			);
			return vendorBillResponse?.data?.vendor_bill || {};
		} catch (error) {
			// Silently fail - we'll use approval data as primary source
			console.warn('Could not fetch vendor bill details, using approval data only:', error);
			return {};
		}
	};

	const mapApprovalDataToDetails = (approvalData, vendorBill) => ({
		...vendorBill, // Vendor bill details (if available) for additional fields like GST
		...approvalData, // This includes changes, image_keys, status, invoice_no, vendor_name, etc.
		audit_id: auditId || approvalData.id,
		vendor_bill_id: entityId || approvalData.vendor_bill_id,
		// Map approval response fields to expected field names
		vendor_bill_no:
			approvalData.invoice_no || vendorBill.vendor_bill_no || approvalData.vendor_bill_no,
		invoice_no: approvalData.invoice_no || vendorBill.invoice_no,
		total_payable_amount: vendorBill.total_payable_amount ?? approvalData.total_payable_amount,
		gst: vendorBill.gst || 0, // GST might only be in vendor bill details
		// Ensure all required fields are present
		vendor_name: approvalData.vendor_name || vendorBill.vendor_name,
		project_name: approvalData.project_name || vendorBill.project_name,
		billing_period: approvalData.billing_period || vendorBill.billing_period,
		created_at: approvalData.created_at || vendorBill.created_at,
		updated_at: approvalData.updated_at || vendorBill.updated_at,
	});

	const fetchApprovalDetails = async () => {
		const uiVersion = getUIVersion();
		const isV2UI = uiVersion === 'new';

		// Use appropriate API based on UI version
		const approvalResponse = await dispatch(
			isV2UI
				? getInvoiceEditApprovalDetailsV2({ vendor_bill_id: Number(entityId) })
				: getInvoiceEditApprovalDetailsV1({ vendor_bill_id: Number(entityId) })
		);

		if (!approvalResponse || approvalResponse.status !== 'success' || !approvalResponse.data) {
			message.error('Approval details not found');
			navigate(APPROVALS_INVOICE_EDIT_PATH);
			return null;
		}

		const approvalData = approvalResponse.data;
		const vendorBill = await fetchVendorBillDetailsOptional();

		return mapApprovalDataToDetails(approvalData, vendorBill);
	};

	const fetchVendorBillDetails = async () => {
		const response = await dispatch(getVendorBillDetailsV2({ id: Number(entityId) }));

		if (!response || !response.data) {
			message.error('Invoice not found');
			navigate(APPROVALS_INVOICE_EDIT_PATH);
			return null;
		}

		const vendorBill = response.data.vendor_bill || {};
		return {
			...vendorBill,
			audit_id: auditId,
		};
	};

	const fetchEditHistory = async () => {
		if (!entityId) return;

		setHistoryLoading(true);
		try {
			const uiVersion = getUIVersion();
			const isV2UI = uiVersion === 'new';

			const historyResponse = await dispatch(
				isV2UI
					? getVendorBillFieldChangesHistoryV2({
							vendor_bill_id: Number(entityId),
							status: ['accepted', 'pending'], // Only show pending and accepted changes, exclude rejected
					  })
					: getVendorBillFieldChangesHistoryV1({
							vendor_bill_id: Number(entityId),
							status: ['accepted', 'pending'], // Only show pending and accepted changes, exclude rejected
					  })
			);

			if (historyResponse && historyResponse.status === 'success') {
				setEditHistory(historyResponse.data || []);
			}
		} catch (error) {
			console.error('Error fetching edit history:', error);
			// Don't show error message - history is optional
		} finally {
			setHistoryLoading(false);
		}
	};

	const fetchDetails = async () => {
		setLoading(true);
		try {
			let detailsData = null;

			// If accessed from approval page, fetch approval details (changes, image_keys, etc.)
			if (fromApproval && entityId) {
				detailsData = await fetchApprovalDetails();
				// If approval details fetch failed, don't proceed
				if (!detailsData) {
					return;
				}
			} else {
				// Fallback: Fetch vendor bill details only (not from approval flow)
				detailsData = await fetchVendorBillDetails();
			}

			if (detailsData) {
				setDetails(detailsData);
				// Fetch edit history after details are loaded
				await fetchEditHistory();
			} else {
				message.error('Invoice not found');
				navigate(APPROVALS_INVOICE_EDIT_PATH);
			}
		} catch (error) {
			console.error('Error fetching invoice details:', error);
			message.error('Failed to fetch invoice details');
		} finally {
			setLoading(false);
		}
	};

	const handleApproveClick = () => {
		setApproveModalVisible(true);
	};

	const handleRejectClick = () => {
		setRejectModalVisible(true);
		setRejectionReason('');
	};

	const handleApproveConfirm = async () => {
		if (!auditId) {
			message.error('Audit ID not found');
			return;
		}

		setActionLoading(true);
		try {
			const uiVersion = getUIVersion();
			const result = await dispatch(
				uiVersion === 'old'
					? acceptVendorBillChangesV1({ audit_id: auditId })
					: acceptVendorBillChanges({ audit_id: auditId })
			);

			if (result.success) {
				setApproveModalVisible(false);
				message.success('Invoice changes accepted successfully');
				navigate(APPROVALS_INVOICE_EDIT_PATH, { state: { refresh: true } });
			} else {
				message.error(result.error || 'Failed to accept invoice changes');
			}
		} catch (error) {
			console.error('Error accepting invoice changes:', error);
			message.error('Failed to accept invoice changes');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRejectConfirm = async () => {
		if (!auditId) {
			message.error('Audit ID not found');
			return;
		}

		setActionLoading(true);
		try {
			const uiVersion = getUIVersion();
			const result = await dispatch(
				uiVersion === 'old'
					? rejectVendorBillChangesV1({
							audit_id: auditId,
							reason: rejectionReason || undefined,
					  })
					: rejectVendorBillChanges({
							audit_id: auditId,
							reason: rejectionReason || undefined,
					  })
			);

			if (result.success) {
				setRejectModalVisible(false);
				setRejectionReason('');
				message.success('Invoice changes rejected successfully');
				navigate(APPROVALS_INVOICE_EDIT_PATH, { state: { refresh: true } });
			} else {
				message.error(result.error || 'Failed to reject invoice changes');
			}
		} catch (error) {
			console.error('Error rejecting invoice changes:', error);
			message.error('Failed to reject invoice changes');
		} finally {
			setActionLoading(false);
		}
	};

	if (loading) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Spin size="large" tip="Loading invoice details..." />
				</Box>
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Text>Invoice not found</Text>
				</Box>
			</PageContainer>
		);
	}

	// Helper function to extract filename from image key
	const extractFileNameFromKey = (imageKey) => {
		if (!imageKey) return null;
		try {
			// The key format is: "uuid-uuid-uuid-uuid-filename.png"
			// (e.g., "05a25220-e19a-413c-8de2-d165ba6584cf-Screenshot from 2026-03-12 12-20-51.png")
			const parts = imageKey.split('-');
			// Check if first part looks like UUID (length > 10) and we have at least 4 parts
			if (parts.length > 4 && parts[0].length > 10) {
				// UUID prefix present, skip first 4 parts (UUID segments) and join the rest
				return parts.slice(4).join('-');
			}
			// No UUID prefix, use the whole key
			return imageKey;
		} catch {
			return imageKey;
		}
	};

	// Helper function to format field name
	const formatFieldName = (field) =>
		field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

	// Helper function to get updated by username
	const getUpdatedBy = (auditEntry) =>
		auditEntry.updated_by_username ||
		auditEntry.created_by_username ||
		auditEntry.updated_by_name ||
		auditEntry.created_by_name ||
		`User ${auditEntry.created_by || auditEntry.updated_by || 'N/A'}`;

	// Transform edit history into table rows
	// Each audit entry can have multiple field changes, so we flatten them
	const transformHistoryToTableRows = () => {
		const rows = [];
		editHistory.forEach((auditEntry) => {
			const changes = auditEntry.changes || {};
			const imageKeys = auditEntry.image_keys || {};
			const updatedBy = getUpdatedBy(auditEntry);
			const updatedAt =
				auditEntry.reviewed_at || auditEntry.created_at || auditEntry.updated_at;

			Object.entries(changes).forEach(([field, change]) => {
				const fieldName = formatFieldName(field);
				const imageKey = imageKeys[field] || null;
				const docFileName = extractFileNameFromKey(imageKey);

				rows.push({
					key: `${auditEntry.id}-${field}`,
					field: fieldName,
					initialValue: change.old,
					updatedValue: change.new,
					updatedBy,
					updated_by_username: updatedBy, // Also include for direct mapping
					updatedAt,
					imageKey: imageKey || null, // Store the image key (filename) for getting signed URL
					docFileName,
				});
			});
		});
		return rows;
	};

	const historyTableRows = transformHistoryToTableRows();

	// Get the current approval status
	// Priority: details.status > audit entry matching auditId > first pending entry
	const getCurrentStatus = () => {
		if (details?.status) {
			return details.status;
		}
		// Check if there's an audit entry matching the auditId
		if (auditId) {
			const matchingEntry = editHistory.find((entry) => entry.id === auditId);
			if (matchingEntry?.status) {
				return matchingEntry.status;
			}
		}
		// Check for any pending entry (most recent)
		const pendingEntry = editHistory.find((entry) => entry.status === 'pending');
		if (pendingEntry) {
			return 'pending';
		}
		// Return the most recent entry's status
		if (editHistory.length > 0) {
			return editHistory[0].status;
		}
		return null;
	};

	const currentStatus = getCurrentStatus();
	const isPending = currentStatus === 'pending';

	// Handle viewing document/image
	const handleViewDocument = async (imageKey, fileName) => {
		if (!imageKey) {
			message.error('No document key provided');
			return;
		}

		setImagePreviewLoading(true);
		setImagePreviewVisible(true);
		setImagePreviewName(fileName || 'Document');

		try {
			// Get signed URL for viewing the image
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'vendor_bill',
						keys: [imageKey],
					},
				})
			);

			// Extract signed URL from response
			const signedUrl =
				(response && response.status === 'success' && response.data?.[0]?.url) ||
				(response?.data &&
					Array.isArray(response.data) &&
					response.data.length > 0 &&
					response.data[0]?.url)
					? response.data[0].url
					: null;

			if (signedUrl) {
				setImagePreviewUrl(signedUrl);
			} else {
				message.error('Failed to load document preview');
				setImagePreviewUrl(null);
			}
		} catch (error) {
			console.error('Error getting signed URL:', error);
			message.error('Failed to load document preview');
			setImagePreviewUrl(null);
		} finally {
			setImagePreviewLoading(false);
		}
	};

	// Handle closing image preview modal
	const handleCloseImagePreview = () => {
		setImagePreviewVisible(false);
		setImagePreviewUrl(null);
		setImagePreviewName('');
	};

	// Table columns for Edit History
	const historyColumns = [
		{
			title: 'FIELD',
			dataIndex: 'field',
			key: 'field',
			width: 200,
		},
		{
			title: 'INITIAL VALUE',
			dataIndex: 'initialValue',
			key: 'initialValue',
			width: 150,
			render: (value) => {
				if (value === null || value === undefined || value === '') return '-';
				if (typeof value === 'number') return formatCurrency(value);
				return String(value);
			},
		},
		{
			title: 'UPDATED VALUE',
			dataIndex: 'updatedValue',
			key: 'updatedValue',
			width: 150,
			render: (value) => {
				if (value === null || value === undefined || value === '') return '-';
				if (typeof value === 'number') return formatCurrency(value);
				return String(value);
			},
		},
		{
			title: 'UPDATED BY',
			dataIndex: 'updated_by_username',
			key: 'updated_by_username',
			width: 150,
		},
		{
			title: 'UPDATED AT',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 180,
			render: (date) => formatDate(date),
		},
		{
			title: 'DOCS',
			dataIndex: 'imageKey',
			key: 'docs',
			width: 200,
			render: (imageKey, record) => {
				if (!imageKey) return '-';
				const fileName = record.docFileName || 'Document';
				return (
					<Button
						type="link"
						icon={<EyeOutlined />}
						onClick={() => handleViewDocument(imageKey, fileName)}
						style={{ padding: 0 }}
					>
						{fileName}
					</Button>
				);
			},
		},
	];

	return (
		<PageContainer $fromApproval={fromApproval}>
			{fromApproval && (
				<HeaderContainer>
					<HeaderLeft>
						<AuditId>Edit ID: {auditId || id || details.id || '-'}</AuditId>
					</HeaderLeft>
					<HeaderRight>
						<CloseIconButton
							icon={<CloseOutlined />}
							onClick={() => navigate(APPROVALS_INVOICE_EDIT_PATH)}
						/>
					</HeaderRight>
				</HeaderContainer>
			)}

			<DetailsCard title="Invoice Details">
				<Row gutter={[24, 16]}>
					<Col span={8}>
						<FieldLabel>Invoice No</FieldLabel>
						<FieldValue>
							<strong>{details.vendor_bill_no || details.invoice_no || '-'}</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Billing Period</FieldLabel>
						<FieldValue>{details.billing_period || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Created at</FieldLabel>
						<FieldValue>{formatDate(details.created_at)}</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Vendor Name</FieldLabel>
						<FieldValue>
							<strong>{details.vendor_name || details.vendor?.name || '-'}</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Total Payable Amount</FieldLabel>
						<FieldValue>
							{formatCurrency(
								details.total_payable_amount || details.initial_invoice_value
							)}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Updated at</FieldLabel>
						<FieldValue>{formatDate(details.updated_at)}</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Project Name</FieldLabel>
						<FieldValue>
							<strong>{details.project_name || details.project?.name || '-'}</strong>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>GST</FieldLabel>
						<FieldValue>{formatCurrency(details.gst || 0)}</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{/* Show Edit History - always show if fromApproval */}
			{fromApproval && (
				<ChangesCard
					title={`EDIT HISTORY (${historyTableRows.length} CHANGE${
						historyTableRows.length !== 1 ? 'S' : ''
					})`}
				>
					{(() => {
						if (historyLoading) {
							return (
								<Box padding="20px" textAlign="center">
									<Spin size="large" tip="Loading edit history..." />
								</Box>
							);
						}
						if (historyTableRows.length > 0) {
							return (
								<Table
									columns={historyColumns}
									dataSource={historyTableRows}
									pagination={false}
									scroll={{ x: 'max-content' }}
									size="small"
								/>
							);
						}
						return (
							<Box padding="20px" textAlign="center">
								<Text fontSize="14px" color="#999">
									No edit history found.
								</Text>
							</Box>
						);
					})()}
				</ChangesCard>
			)}

			{/* Show Accept/Reject buttons only when status is pending */}
			{fromApproval && auditId && isPending && (
				<ActionButtonContainer>
					<ACL action="update" module="vendor_bill_changes_reject">
						<RejectButtonStyled onClick={handleRejectClick} disabled={actionLoading}>
							<CloseOutlined />
							Reject
						</RejectButtonStyled>
					</ACL>
					<ACL action="update" module="vendor_bill_changes_accept">
						<ApproveButtonStyled onClick={handleApproveClick} disabled={actionLoading}>
							<CheckOutlined />
							Accept Changes
						</ApproveButtonStyled>
					</ACL>
				</ActionButtonContainer>
			)}

			{/* Approve Modal */}
			<Modal
				title="Accept Invoice Changes"
				open={approveModalVisible}
				onOk={handleApproveConfirm}
				onCancel={() => {
					setApproveModalVisible(false);
				}}
				okText="Accept Changes"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading,
				}}
			>
				<Box>
					<Text
						display="block"
						style={{
							fontSize: '14px',
							color: '#666',
							marginBottom: 24,
							lineHeight: 1.5,
						}}
					>
						This will accept all pending changes to the vendor bill and recalculate the
						total payable amount. You won&apos;t be able to revert this change.
					</Text>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Invoice Changes"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading,
					danger: true,
				}}
			>
				<Box>
					<Text fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject these invoice edit changes? The vendor bill
						will remain unchanged.
					</Text>
					<Box mb={3}>
						<Text fontSize="14px" color="#333" mb={1} display="block">
							Rejection Reason (Optional)
						</Text>
						<TextArea
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder="Enter reason for rejection (optional)..."
							maxLength={1000}
							showCount
						/>
					</Box>
				</Box>
			</Modal>

			{/* Image Preview Modal */}
			<Modal
				title={imagePreviewName || 'Document Preview'}
				open={imagePreviewVisible}
				onCancel={handleCloseImagePreview}
				footer={null}
				width="90vw"
				centered
				destroyOnClose
				closeIcon={<CloseOutlined />}
			>
				{(() => {
					if (imagePreviewLoading) {
						return (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								minHeight="400px"
							>
								<Spin size="large" tip="Loading document..." />
							</Box>
						);
					}
					if (imagePreviewUrl) {
						const isPdf =
							imagePreviewName?.toLowerCase().endsWith('.pdf') ||
							imagePreviewName?.toLowerCase().includes('.pdf');

						if (isPdf) {
							return (
								<Box textAlign="center" padding="10px">
									<iframe
										src={imagePreviewUrl}
										title={imagePreviewName || 'Document'}
										style={{ width: '100%', height: '70vh', border: 'none' }}
									/>
								</Box>
							);
						}

						return (
							<Box textAlign="center" padding="20px">
								<img
									src={imagePreviewUrl}
									alt={imagePreviewName || 'Document'}
									style={{
										maxWidth: '100%',
										maxHeight: '70vh',
										objectFit: 'contain',
									}}
									onError={(e) => {
										console.error('Image load error:', e);
										message.error(
											'Failed to load image. The file may be corrupted or the URL has expired.'
										);
									}}
								/>
							</Box>
						);
					}
					return (
						<Box padding="40px" textAlign="center">
							<Text type="secondary">No document URL available</Text>
						</Box>
					);
				})()}
			</Modal>
		</PageContainer>
	);
}

export default InvoiceEditApprovalDetailsPageV2;
